import { MAIN_PLAYER_ID, SILENT_PLAYER_ID } from '@/pages/AdvancedEditor/components/panels/script/TextSection';
import { useUploadActivePageTtsCache } from '@/pages/AdvancedEditor/hooks/useUploadTtsCache';
import useTtsData from '@/services/tts/tokenManager';
import { getVoiceId } from '@/services/tts/tts-data';
import { AudioError, DEFAULT_VOICE, DEFAULT_VOICE_AGENTS, languageToVoicesMap } from '@/state/presenters';
import { ExtendedLocation, WordLevelTimestamp } from '@/utils/types';
import { isWordLevelTimestampValid } from '@/utils/utils';
import { getSsml } from '@d-id/ssml';
import { IVoice, Providers, VoiceConfig } from '@d-id/types';
import {
    AudioConfig,
    CancellationDetails,
    CancellationErrorCode,
    SpeakerAudioDestination,
    SpeechConfig,
    SpeechSynthesizer,
} from 'microsoft-cognitiveservices-speech-sdk';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { activePlayerIdAtom, PlayerMode, playerModeAtom } from '../useAudioPlayer';
import { Audio24Khz48KBitRateMonoMp3, CANCELED, SpeechSDK } from './constants';

let player: SpeakerAudioDestination | null = null;
let synthesizer: SpeechSynthesizer | null = null;

interface PlayParams {
    useIndicators?: boolean;
    volume: number;
    onAudioEnd?: () => void;
    uploadCache?: boolean;
    force?: boolean;
}

interface Props {
    voice: IVoice | null;
    text: string;
    language: string | null;
    voiceConfig?: VoiceConfig;
    playerId?: string;
    ttsError?: AudioError | null;
    setTtsError?: (error: AudioError | null) => void;
}

export const useAzureSynthesizer = ({ voice, text, language, voiceConfig, playerId, ttsError, setTtsError }: Props) => {
    const locationState = useLocation() as ExtendedLocation;
    const isAgents = locationState.pathname?.includes('agents') ?? false;

    const { data } = useTtsData();
    const languageToVoices = useRecoilValue(languageToVoicesMap);

    const selectedLanguage = voice?.language ?? language ?? '';
    const defaultVoice = isAgents ? DEFAULT_VOICE_AGENTS : DEFAULT_VOICE;

    const [playerMode, setPlayerMode] = useRecoilState(playerModeAtom);
    const [activePlayerId, setActivePlayerId] = useRecoilState(activePlayerIdAtom);
    const uploadCachedTts = useUploadActivePageTtsCache();

    useEffect(() => {
        if (playerId === MAIN_PLAYER_ID) {
            setPlayerMode(PlayerMode.NotLoaded);
            setTtsError?.(null);
        }
    }, [text, voice, voiceConfig]);

    const play = useMemo(() => {
        const wordLevelTimestamps: WordLevelTimestamp[] = [];
        const credentials = data?.credentials;

        if (!!credentials && voice && SpeechSDK) {
            if (playerMode === PlayerMode.NotLoaded && player) {
                player.pause();
                player.close();
                synthesizer?.close();
                synthesizer = null;
            }

            return ({ useIndicators = true, volume = 1, onAudioEnd, uploadCache = true, force = false }: PlayParams) => {
                if (force || playerMode === PlayerMode.NotLoaded || (playerMode === PlayerMode.Ready && activePlayerId !== playerId)) {
                    setPlayerMode(PlayerMode.Loading);
                    setActivePlayerId(playerId!);

                    const voiceName = ![Providers.Microsoft, Providers.AzureOpenAi].includes(voice.provider)
                        ? getVoiceId(voice, text, languageToVoices, selectedLanguage) || defaultVoice.id
                        : voice.id;
                    const { token, region } =
                        voice.provider === Providers.AzureOpenAi ? credentials[Providers.AzureOpenAi] : credentials[Providers.Microsoft];
                    const speechConfig = SpeechConfig.fromAuthorizationToken(token, region);
                    speechConfig.speechSynthesisOutputFormat = Audio24Khz48KBitRateMonoMp3;

                    player = new SpeakerAudioDestination();
                    synthesizer = new SpeechSynthesizer(speechConfig, AudioConfig.fromSpeakerOutput(player));

                    player.internalAudio.volume = volume;
                    player.onAudioEnd = () => {
                        onAudioEnd?.();
                        setPlayerMode(PlayerMode.Ready);
                        setActivePlayerId('');
                    };

                    synthesizer.synthesisStarted = () => setPlayerMode(useIndicators ? PlayerMode.Playing : PlayerMode.Ready);
                    synthesizer.SynthesisCanceled = (_, e) => {
                        if (e.result.errorDetails.startsWith('The processed audio has exceeded the configured maximum media duration of')) {
                            setTtsError?.('long-duration');
                        } else if (
                            e.result.reason === CANCELED &&
                            CancellationDetails.fromResult(e.result).ErrorCode == CancellationErrorCode.ConnectionFailure
                        ) {
                            setTtsError?.('no-connection');
                        } else {
                            setTtsError?.('unknown');
                        }
                    };

                    synthesizer.synthesisCompleted = async (_, e) => {
                        if (e.result.audioData.byteLength === 0) {
                            setTtsError?.('language');
                            setPlayerMode(PlayerMode.NotLoaded);
                            setActivePlayerId('');
                            return;
                        }

                        const duration = Math.ceil(e.result.audioDuration / 10000 / 1000);

                        if (playerId && [MAIN_PLAYER_ID, SILENT_PLAYER_ID].includes(playerId)) {
                            if (uploadCache) {
                                if (isWordLevelTimestampValid(wordLevelTimestamps)) {
                                    await uploadCachedTts(e.result.audioData, text, duration, wordLevelTimestamps);
                                } else {
                                    await uploadCachedTts(e.result.audioData, text, duration);
                                }
                            }
                        }
                    };

                    synthesizer.wordBoundary = async (_, event: any) => {
                        const wordTimestamp = {
                            word: event.text,
                            startTime: event.audioOffset,
                            endTime: event.audioOffset + event.duration,
                        };
                        wordLevelTimestamps.push(wordTimestamp);
                    };

                    synthesizer.speakSsmlAsync(
                        getSsml(text, voiceName, { ...voiceConfig, volume: 100 }),
                        () => {
                            synthesizer?.close();
                            synthesizer = null;
                        },
                        (error: any) => {
                            setTtsError?.(error);
                            setPlayerMode(PlayerMode.NotLoaded);
                            setActivePlayerId('');
                            synthesizer?.close();
                            synthesizer = null;
                        }
                    );
                } else if (player) {
                    player.pause.call(player);
                    player.internalAudio.currentTime = 0;
                    player.internalAudio.volume = volume;
                    player.resume.call(player);
                    setPlayerMode(useIndicators ? PlayerMode.Playing : PlayerMode.Ready);
                }
            };
        }

        return () => {};
    }, [playerMode, data, text, voice?.id, voiceConfig, activePlayerId, uploadCachedTts]);

    const pause = useCallback(() => {
        player?.pause.call(player);
        setPlayerMode(PlayerMode.Ready);
        setActivePlayerId('');
    }, []);

    return { play, pause, playerMode, error: ttsError };
};
